/**
 * CommandPalette — globale Suche/Befehlspalette (⌘K / Strg-K).
 *
 * REFERENZ-IMPLEMENTIERUNG laut UX-Konzept (RMNC-Leitstand/docs/UX_KONZEPT.md §3),
 * Bedienmuster 1:1 aus rmnc-stammdaten (Branch feature/ux-appswitcher-cmdk),
 * dort wiederum aus rmnc-personal (netzfabriks-erp) portiert:
 * dependency-frei (kein cmdk/kbar/Radix), Overlay + Scoring + Debounce.
 *
 * Zwei-Schichten-Modell:
 *  - Seiten + Aktionen + App-Wechsel: client-seitig, synchron pro Tastendruck.
 *  - Entitäten: 180 ms debounced an GET /api/v1/search?q= (Server, ILIKE,
 *    Rechte-gefiltert), gruppiert nach Entitätstyp.
 *
 * Tastatur: ⌘/Strg+K Toggle (im Layout registriert), ↑/↓ navigieren,
 * Enter öffnen, Escape schließen.
 *
 * Übernahme in andere Apps: Datei kopieren, PAGES/ACTIONS anpassen —
 * der Server-Teil läuft überall gegen denselben Endpoint-Vertrag.
 */
import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { api } from "@/api/client"

// ---------------------------------------------------------------------------
// Datentypen + statische Quellen
// ---------------------------------------------------------------------------

interface Command {
  id: string
  title: string
  subtitle?: string
  group: string
  /** Interne SPA-Route ODER (bei App-Wechsel) absolute URL */
  href: string
  external?: boolean
  /** Versteckte Suchbegriffe fürs Scoring */
  keywords?: string
}

interface SearchHit {
  typ: string
  id: string
  titel: string
  untertitel: string | null
  route: string
}

const PAGES: Command[] = [
  { id: "page-dashboard", title: "Dashboard", href: "/dashboard", group: "Seiten",
    keywords: "energiemonitoring uebersicht zeitraum" },
  { id: "page-zaehler", title: "Zähler", href: "/zaehler", group: "Seiten",
    keywords: "zaehlerstamm hierarchie unterzaehler erfassung" },
  { id: "page-energiearten", title: "Energiearten & Preise", href: "/energiearten", group: "Seiten",
    keywords: "strom gas wasser preishistorie euro" },
  { id: "page-seu", title: "SEU / ISO 50001", href: "/seu", group: "Seiten",
    keywords: "significant energy use einsatzstelle schwellwert reporting" },
  { id: "page-produktionsdaten", title: "Produktionsdaten", href: "/produktionsdaten", group: "Seiten",
    keywords: "tageskennzahlen nitrierung chips menge" },
]

const ACTIONS: Command[] = [
  { id: "act-zaehlerstand-erfassen", title: "Zählerstand erfassen", href: "/zaehler", group: "Aktionen",
    keywords: "einbuchen verbrauch erfassung neu" },
  { id: "act-preis-anlegen", title: "Preis anlegen", href: "/energiearten", group: "Aktionen",
    keywords: "energiepreis anlegen neu euro" },
  { id: "act-neue-seu", title: "SEU-Einsatzstelle anlegen", href: "/seu", group: "Aktionen",
    keywords: "significant energy use anlegen neu" },
  { id: "act-produktionsdaten-erfassen", title: "Produktionsdaten erfassen", href: "/produktionsdaten",
    group: "Aktionen", keywords: "tageskennzahlen anlegen neu" },
]

/** App-Wechsel-Ziele (Titel wie Portal-Kacheln); Links kommen aus /links.json. */
const APP_TARGETS: { key: string; title: string }[] = [
  { key: "portal", title: "Portal" },
  { key: "leitstand", title: "Leitstand" },
  { key: "personal", title: "Personal" },
  { key: "technik", title: "Technik" },
  { key: "stammdaten", title: "Stammdaten" },
  { key: "dokubase", title: "Dokubase" },
]

// ---------------------------------------------------------------------------
// Scoring — 1:1 aus rmnc-stammdaten/rmnc-personal (netzfabriks-erp).
// ---------------------------------------------------------------------------

function normalize(value: string): string {
  return value.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
}

function acronym(value: string): string {
  return normalize(value).split(/[^a-z0-9]+/).filter(Boolean).map((p) => p[0]).join("")
}

function fuzzyMatch(needle: string, haystack: string): boolean {
  if (!needle) return true
  let i = 0
  for (const c of haystack) {
    if (c === needle[i]) i++
    if (i === needle.length) return true
  }
  return false
}

function scoreItem(query: string, item: Command): number | null {
  const q = normalize(query.trim())
  if (!q) return 0
  const title = normalize(item.title)
  const haystack = `${title} ${normalize(item.subtitle ?? "")} ${normalize(item.keywords ?? "")} ${normalize(item.href)}`
  if (title === q) return 1000
  if (title.startsWith(q)) return 900
  if (haystack.includes(q)) return 700
  if (haystack.split(/[^a-z0-9]+/).some((w) => w.startsWith(q))) return 600
  if (acronym(`${item.subtitle ?? ""} ${item.title}`).startsWith(q)) return 500
  if (q.length <= 3 && fuzzyMatch(q, haystack)) return 200
  return null
}

function scoreList(query: string, list: Command[]): Command[] {
  return list
    .flatMap((item) => {
      const score = scoreItem(query, item)
      return score === null ? [] : [{ item, score }]
    })
    .sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title, "de"))
    .map(({ item }) => item)
}

// ---------------------------------------------------------------------------
// Komponente
// ---------------------------------------------------------------------------

export interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** App-Wechsel-Links (aus /links.json, siehe AppSwitcher) */
  links?: Record<string, string> | null
  /**
   * Geschäftsführungs-Leserolle (app:energie:geschaeftsfuehrung, ARCHITEKTUR.md §5):
   * sieht NUR SEU/ISO-50001 — client-seitig blenden wir Seiten/Aktionen der
   * operativen Module konsistent zur Navigation aus (App.tsx useSichtbareModule).
   * Durchgesetzt wird das ohnehin serverseitig (backend/app/api/v1/search.py).
   */
  hasOnlySeuAccess?: boolean
}

export function CommandPalette({ open, onOpenChange, links, hasOnlySeuAccess }: CommandPaletteProps) {
  const navigate = useNavigate()
  const [query, setQuery] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")
  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const trimmed = query.trim()
  const trimmedDebounced = debouncedQuery.trim()

  const sichtbarePages = useMemo(
    () => (hasOnlySeuAccess ? PAGES.filter((p) => p.id === "page-seu") : PAGES),
    [hasOnlySeuAccess],
  )
  const sichtbareActions = useMemo(
    () => (hasOnlySeuAccess ? ACTIONS.filter((a) => a.id === "act-neue-seu") : ACTIONS),
    [hasOnlySeuAccess],
  )

  // 180 ms Debounce — betrifft nur die Server-Suche.
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query.trim()), 180)
    return () => window.clearTimeout(handle)
  }, [query])

  const appCommands = useMemo<Command[]>(
    () =>
      APP_TARGETS.flatMap((t) => {
        const href = links?.[t.key]
        if (!href) return []
        return [{
          id: `app-${t.key}`,
          title: `Wechseln zu: ${t.title}`,
          href,
          external: true,
          group: "Apps",
          keywords: "app wechseln oeffnen portal",
        }]
      }),
    [links],
  )

  const pageItems = useMemo(() => scoreList(query, sichtbarePages), [query, sichtbarePages])
  const actionItems = useMemo(
    () => (trimmed.length >= 1 ? scoreList(query, sichtbareActions) : []),
    [query, trimmed.length, sichtbareActions],
  )
  const appItems = useMemo(
    () => (trimmed.length >= 1 ? scoreList(query, appCommands) : []),
    [query, appCommands, trimmed.length],
  )

  // Entitäten: debounced Server-Suche (einzige Netzwerk-Quelle)
  const { data: serverHits, isFetching } = useQuery({
    queryKey: ["command-palette", "search", trimmedDebounced],
    queryFn: async () =>
      (await api.get<{ data: SearchHit[] }>("/search", { params: { q: trimmedDebounced } })).data
        .data,
    enabled: open && trimmedDebounced.length >= 2,
    staleTime: 30_000,
  })

  const entityGroups = useMemo(() => {
    // Anti-Flacker: alte Treffer nicht zeigen, solange der Debounce läuft.
    if (trimmed.length < 2 || trimmedDebounced !== trimmed) return []
    const byTyp = new Map<string, Command[]>()
    for (const hit of serverHits ?? []) {
      const cmd: Command = {
        id: `ent-${hit.typ}-${hit.id}`,
        title: hit.titel,
        subtitle: hit.untertitel ?? undefined,
        href: hit.route,
        group: hit.typ,
      }
      byTyp.set(hit.typ, [...(byTyp.get(hit.typ) ?? []), cmd])
    }
    return Array.from(byTyp, ([label, items]) => ({ label, items }))
  }, [serverHits, trimmed, trimmedDebounced])

  const itemGroups = useMemo(() => {
    const staticGroups =
      trimmed.length === 0
        ? [{ label: "Seiten", items: pageItems }]
        : [
            { label: "Aktionen", items: actionItems },
            { label: "Seiten", items: pageItems },
            ...entityGroups,
            { label: "Apps", items: appItems },
          ]
    return staticGroups.filter((g) => g.items.length > 0)
  }, [trimmed.length, pageItems, actionItems, entityGroups, appItems])

  const items = useMemo(() => itemGroups.flatMap((g) => g.items), [itemGroups])

  // Reset beim Öffnen
  useEffect(() => {
    if (open) {
      setQuery("")
      setDebouncedQuery("")
      setActiveIdx(0)
      setTimeout(() => inputRef.current?.focus(), 10)
    }
  }, [open])

  useEffect(() => {
    if (activeIdx >= items.length) setActiveIdx(Math.max(0, items.length - 1))
  }, [items.length, activeIdx])

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`)
      ?.scrollIntoView({ block: "nearest" })
  }, [activeIdx])

  function selectItem(item: Command) {
    onOpenChange(false)
    if (item.external) window.location.assign(item.href)
    else navigate(item.href)
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIdx((i) => Math.min(items.length - 1, i + 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIdx((i) => Math.max(0, i - 1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      const item = items[activeIdx]
      if (item) selectItem(item)
    } else if (e.key === "Escape") {
      onOpenChange(false)
    }
  }

  if (!open) return null

  const loading = isFetching && trimmed.length >= 2

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[15vh] backdrop-blur-sm"
      onClick={() => onOpenChange(false)}
      role="dialog"
      aria-modal="true"
      aria-label="Globale Suche"
    >
      <div
        className="mx-4 w-full max-w-xl overflow-hidden rounded-lg border border-slate-200 bg-white text-slate-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Suchfeld */}
        <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" className="shrink-0 text-slate-400" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setActiveIdx(0)
            }}
            onKeyDown={handleKey}
            placeholder="Suchen oder navigieren…"
            aria-label="Suchen"
            className="flex-1 bg-transparent py-1.5 text-sm outline-none placeholder:text-slate-400"
          />
          {loading && (
            <div className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
          )}
          <kbd className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">ESC</kbd>
        </div>

        {/* Ergebnisliste */}
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-1" role="listbox">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-500">
              {loading ? "Suche läuft…" : "Nichts gefunden."}
            </div>
          )}
          {(() => {
            let idx = 0
            return itemGroups.map((group) => (
              <div key={group.label}>
                {itemGroups.length > 1 && (
                  <div className="px-3 pb-1 pt-2 text-[10px] font-medium uppercase text-slate-400">
                    {group.label}
                  </div>
                )}
                {group.items.map((item) => {
                  const currentIdx = idx++
                  const isActive = currentIdx === activeIdx
                  return (
                    <button
                      key={item.id}
                      data-idx={currentIdx}
                      role="option"
                      aria-selected={isActive}
                      onClick={() => selectItem(item)}
                      onMouseEnter={() => setActiveIdx(currentIdx)}
                      className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm ${
                        isActive ? "bg-slate-100" : "hover:bg-slate-50"
                      }`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{item.title}</span>
                        {item.subtitle && (
                          <span className="block truncate text-[11px] text-slate-500">
                            {item.subtitle}
                          </span>
                        )}
                      </span>
                      {!item.subtitle && (
                        <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                          {item.group}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            ))
          })()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-200 px-3 py-2 text-[10px] text-slate-500">
          <span>
            {items.length} Eintrag{items.length === 1 ? "" : "e"}
          </span>
          <span className="flex items-center gap-2">
            <span>↑↓ navigieren</span>
            <span>↵ öffnen</span>
          </span>
        </div>
      </div>
    </div>
  )
}
